// services/MessageService.js - ReactJS (web)
import api from '../api/api';

class MessageService {
  // ==================== PUBLIC ROUTES ====================

  /**
   * Create a new message (public - no auth required)
   * POST /api/messages/create-message
   */
  async createMessage(data) {
    try {
      const response = await api.post('/messages/create-message', data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== ADMIN ROUTES ====================

  /**
   * Get all messages with enhanced features
   * GET /api/messages/messages
   */
  async getAllMessages(params = {}) {
    try {
      const response = await api.get('/messages/', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get message by ID with enhanced data
   * GET /api/messages/message/:id
   */
  async getMessageById(id) {
    try {
      const response = await api.get(`/messages/message/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update message with enhanced features
   * PUT /api/messages/update-message/:id
   */
  async updateMessage(id, data) {
    try {
      const response = await api.put(`/messages/update-message/${id}`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get urgent messages
   * GET /api/messages/messages/urgent
   */
  async getUrgentMessages(page = 1, limit = 20) {
    try {
      const response = await api.get('/messages/urgent', {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get overdue messages
   * GET /api/messages/messages/overdue
   */
  async getOverdueMessages(hours = 24, page = 1, limit = 20) {
    try {
      const response = await api.get('/messages/overdue', {
        params: { hours, page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get escalated messages
   * GET /api/messages/messages/escalated
   */
  async getEscalatedMessages(page = 1, limit = 20) {
    try {
      const response = await api.get('/messages/escalated', {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get message timeline (for customer view)
   * GET /api/messages/message/:id/timeline
   */
  async getMessageTimeline(id) {
    try {
      const response = await api.get(`/messages/message/${id}/timeline`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get message analytics
   * GET /api/messages/message/analytics
   */
  async getMessageAnalytics(startDate, endDate) {
    try {
      const response = await api.get('/messages/message/analytics', {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get message statistics
   * GET /api/messages/message/statistics
   */
  async getMessageStatistics() {
    try {
      const response = await api.get('/messages/message/statistics');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Send manual reply to message (with email notification)
   * POST /api/messages/message/:id/reply
   */
  async sendReply(id, content, sentBy = 'admin') {
    try {
      const response = await api.post(`/messages/message/${id}/reply`, { content, sentBy });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Resend message notification
   * POST /api/messages/message/:id/resend-notification
   */
  async resendNotification(id) {
    try {
      const response = await api.post(`/messages/message/${id}/resend-notification`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    let errorMessage = ' ';
    
    if (error.response?.data?.data?.error?.message) {
      errorMessage = error.response.data.data.error.message;
    } else if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status,
      data: error.response?.data
    };
  }

  /**
   * Get message types
   */
  getMessageTypes() {
    return ['inquiry', 'complaint', 'feedback', 'request', 'support', 'other'];
  }

  /**
   * Get message statuses
   */
  getMessageStatuses() {
    return ['pending', 'read', 'responded', 'in_progress', 'resolved', 'closed'];
  }

  /**
   * Get priority levels
   */
  getPriorityLevels() {
    return ['low', 'medium', 'high', 'urgent'];
  }

  /**
   * Get categories
   */
  getCategories() {
    return ['general', 'technical', 'billing', 'account', 'service', 'donation', 'other'];
  }

  /**
   * Get status badge color
   */
  getStatusBadgeColor(status) {
    const colors = {
      pending: '#FFA500',
      read: '#3498DB',
      responded: '#9B59B6',
      in_progress: '#F39C12',
      resolved: '#2ECC71',
      closed: '#95A5A6'
    };
    return colors[status] || '#95A5A6';
  }

  /**
   * Get priority badge color
   */
  getPriorityBadgeColor(priority) {
    const colors = {
      low: '#2ECC71',
      medium: '#F1C40F',
      high: '#E67E22',
      urgent: '#E74C3C'
    };
    return colors[priority] || '#95A5A6';
  }

  /**
   * Get status label
   */
  getStatusLabel(status) {
    const labels = {
      pending: 'Pending',
      read: 'Read',
      responded: 'Responded',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed'
    };
    return labels[status] || status;
  }

  /**
   * Get priority label
   */
  getPriorityLabel(priority) {
    const labels = {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      urgent: 'Urgent'
    };
    return labels[priority] || priority;
  }

  /**
   * Validate message data
   */
  validateMessageData(data) {
    const errors = [];

    if (!data.name) {
      errors.push('Name is required');
    } else if (data.name.length < 2) {
      errors.push('Name must be at least 2 characters');
    }

    if (!data.email) {
      errors.push('Email is required');
    } else if (!this.isValidEmail(data.email)) {
      errors.push('Invalid email format');
    }

    if (!data.subject) {
      errors.push('Subject is required');
    } else if (data.subject.length < 5) {
      errors.push('Subject must be at least 5 characters');
    }

    if (!data.message) {
      errors.push('Message is required');
    } else if (data.message.length < 20) {
      errors.push('Message must be at least 20 characters');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate email format
   */
  isValidEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  /**
   * Format message for display
   */
  formatMessage(message) {
    if (!message) return null;

    return {
      ...message,
      id: message._id,
      statusLabel: this.getStatusLabel(message.status),
      statusColor: this.getStatusBadgeColor(message.status),
      priorityLabel: this.getPriorityLabel(message.priority),
      priorityColor: this.getPriorityBadgeColor(message.priority),
      createdAt: message.createdAt ? new Date(message.createdAt) : null,
      updatedAt: message.updatedAt ? new Date(message.updatedAt) : null,
      readAt: message.readAt ? new Date(message.readAt) : null,
      respondedAt: message.respondedAt ? new Date(message.respondedAt) : null,
      resolvedAt: message.resolvedAt ? new Date(message.resolvedAt) : null,
      closedAt: message.closedAt ? new Date(message.closedAt) : null,
      timeAgo: message.createdAt ? this.getTimeAgo(message.createdAt) : null,
      ageInHours: message.createdAt ? this.getAgeInHours(message.createdAt) : null,
      hasResponses: message.responses && message.responses.length > 0,
      responseCount: message.responses?.length || 0,
      hasNotes: message.notes && message.notes.length > 0,
      noteCount: message.notes?.length || 0,
      isUrgent: message.priority === 'urgent' || message.priority === 'high',
      isOverdue: this.isOverdue(message),
      urgencyScore: this.calculateUrgencyScore(message)
    };
  }

  /**
   * Get time ago string
   */
  getTimeAgo(date) {
    if (!date) return null;
    const now = new Date();
    const diff = now - new Date(date);
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (weeks < 4) return `${weeks}w ago`;
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  }

  /**
   * Get age in hours
   */
  getAgeInHours(date) {
    if (!date) return 0;
    const now = new Date();
    return (now - new Date(date)) / (1000 * 60 * 60);
  }

  /**
   * Check if message is overdue
   */
  isOverdue(message) {
    if (!message || !message.createdAt) return false;
    if (message.status === 'resolved' || message.status === 'closed') return false;
    
    const hours = this.getAgeInHours(message.createdAt);
    return hours > 24; // Overdue after 24 hours
  }

  /**
   * Calculate urgency score
   */
  calculateUrgencyScore(message) {
    if (!message) return 0;
    
    let score = 0;
    
    // Priority score
    const priorityScores = {
      low: 1,
      medium: 2,
      high: 3,
      urgent: 4
    };
    score += priorityScores[message.priority] || 0;
    
    // Age score (increases with age)
    const ageInHours = this.getAgeInHours(message.createdAt);
    score += Math.min(Math.floor(ageInHours / 6), 4); // Max 4 points
    
    // Escalated score
    if (message.isEscalated) score += 2;
    
    // Status score
    if (message.status === 'pending' || message.status === 'read') score += 1;
    
    return Math.min(score, 10); // Max score of 10
  }

  /**
   * Build query params for message search
   */
  buildQueryParams(filters = {}) {
    const params = {};

    if (filters.status) {
      params.status = filters.status;
    }

    if (filters.messageType) {
      params.messageType = filters.messageType;
    }

    if (filters.priority) {
      params.priority = filters.priority;
    }

    if (filters.category) {
      params.category = filters.category;
    }

    if (filters.email) {
      params.email = filters.email;
    }

    if (filters.serviceProviderId) {
      params.serviceProviderId = filters.serviceProviderId;
    }

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.isEscalated !== undefined) {
      params.isEscalated = filters.isEscalated;
    }

    if (filters.includeUrgent !== undefined) {
      params.includeUrgent = filters.includeUrgent;
    }

    if (filters.dateFrom) {
      params.dateFrom = filters.dateFrom;
    }

    if (filters.dateTo) {
      params.dateTo = filters.dateTo;
    }

    if (filters.page) {
      params.page = filters.page;
    }

    if (filters.limit) {
      params.limit = filters.limit;
    }

    if (filters.sortBy) {
      params.sortBy = filters.sortBy;
    }

    if (filters.sortOrder) {
      params.sortOrder = filters.sortOrder;
    }

    return params;
  }

  /**
   * Get message summary
   */
  getMessageSummary(message) {
    if (!message) return '';
    const maxLength = 150;
    const text = message.message || '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  /**
   * Check if status transition is allowed
   */
  isStatusTransitionAllowed(currentStatus, newStatus) {
    const transitions = {
      pending: ['read', 'responded', 'in_progress', 'resolved', 'closed'],
      read: ['responded', 'in_progress', 'resolved', 'closed'],
      responded: ['in_progress', 'resolved', 'closed'],
      in_progress: ['responded', 'resolved', 'closed'],
      resolved: ['closed'],
      closed: []
    };
    
    return transitions[currentStatus]?.includes(newStatus) || false;
  }

  /**
   * Get allowed transitions for a status
   */
  getAllowedTransitions(status) {
    const transitions = {
      pending: ['read', 'responded', 'in_progress', 'resolved', 'closed'],
      read: ['responded', 'in_progress', 'resolved', 'closed'],
      responded: ['in_progress', 'resolved', 'closed'],
      in_progress: ['responded', 'resolved', 'closed'],
      resolved: ['closed'],
      closed: []
    };
    
    return transitions[status] || [];
  }
}

export default new MessageService();